import type { Document, ComponentDef, PageInstance } from './document.js';

/**
 * Pure template renderer — no DOM, no I/O, runs identically in the API
 * (PDF generation) and the browser preview. That symmetry is the whole
 * point: unlike S-Docs, what you preview is byte-for-byte what prints.
 *
 * Binding syntax inside `kind: 'html'` component sources:
 *   {{ path.to.value }}                  escaped interpolation
 *   {{{ path.to.html }}}                 raw HTML (rich-text fields)
 *   {{ path | currency }}                helpers: currency, number, date
 *   {{#each path}} … {{/each}}           component-level loop; inside the
 *                                        block `this.*` is the item, `@index`
 *                                        the 0-based index
 *   {{#if path}} … {{/if}}               truthy conditional
 */

export type Scope = Record<string, unknown>;

const HELPERS: Record<string, (v: unknown) => string> = {
  currency: (v) =>
    typeof v === 'number'
      ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: v % 1 ? 2 : 0 })
      : String(v ?? ''),
  number: (v) => (typeof v === 'number' ? v.toLocaleString('en-US') : String(v ?? '')),
  date: (v) => {
    const d = typeof v === 'string' || v instanceof Date ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : String(v ?? '');
  },
};

export function resolvePath(scope: Scope, path: string): unknown {
  if (path === 'this') return scope['this'];
  const parts = path.split('.');
  let cur: unknown = parts[0] === 'this' ? scope['this'] : scope;
  for (const part of parts[0] === 'this' ? parts.slice(1) : parts) {
    if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function applyHelper(value: unknown, helper: string | undefined): string {
  if (!helper) return escapeHtml(value);
  const fn = HELPERS[helper.trim()];
  return fn ? escapeHtml(fn(value)) : escapeHtml(value);
}

/** innermost-first block expansion so nested {{#each}}/{{#if}} work */
const BLOCK_RE = /\{\{#(each|if)\s+([\w.@]+)\}\}((?:(?!\{\{#(?:each|if))[\s\S])*?)\{\{\/\1\}\}/;

export function renderTemplate(source: string, scope: Scope): string {
  let out = source;
  // expand blocks until none remain (innermost matched first by the regex guard)
  for (let guard = 0; guard < 100; guard++) {
    const m = BLOCK_RE.exec(out);
    if (!m) break;
    const [whole, kind, path, body] = m as unknown as [string, string, string, string];
    let replacement = '';
    if (kind === 'each') {
      const items = resolvePath(scope, path);
      if (Array.isArray(items)) {
        replacement = items
          .map((item, i) =>
            renderTemplate(body, { ...scope, this: item, '@index': i, '@first': i === 0 }),
          )
          .join('');
      }
    } else {
      const v = resolvePath(scope, path);
      const truthy = Array.isArray(v) ? v.length > 0 : Boolean(v);
      replacement = truthy ? renderTemplate(body, scope) : '';
    }
    out = out.slice(0, m.index) + replacement + out.slice(m.index + whole.length);
  }
  // raw interpolation first (triple), then escaped (double)
  out = out.replace(/\{\{\{\s*([\w.@]+)\s*\}\}\}/g, (_, p: string) => String(resolvePath(scope, p) ?? ''));
  out = out.replace(/\{\{\s*([\w.@]+)\s*(?:\|\s*(\w+)\s*)?\}\}/g, (_, p: string, h?: string) =>
    applyHelper(resolvePath(scope, p), h),
  );
  return out;
}

function renderPage(page: PageInstance, component: ComponentDef, scope: Scope): string {
  const inner = renderTemplate(component.source, scope);
  return `<section class="page" data-page-id="${escapeHtml(page.id)}">${inner}</section>`;
}

export interface RenderResult {
  html: string;
  /** pages after loop expansion, in order — useful for TOC/page numbers */
  pageCount: number;
  warnings: string[];
}

export function renderDocument(doc: Document, components: ComponentDef[]): RenderResult {
  const byId = new Map(components.map((c) => [c.id, c]));
  const warnings: string[] = [];
  const sections: string[] = [];

  for (const page of doc.pages) {
    const component = byId.get(page.componentId);
    if (!component) {
      warnings.push(`page "${page.id}": unknown component "${page.componentId}"`);
      continue;
    }
    if (component.kind !== 'html') {
      warnings.push(`page "${page.id}": component "${component.id}" is kind=${component.kind}; server render supports html (react renders in the sandbox preview)`);
      continue;
    }
    const base: Scope = { ...doc.data, ...(page.props ?? {}), doc: { name: doc.name } };
    if (page.repeat) {
      const items = resolvePath(base, page.repeat.over);
      if (!Array.isArray(items)) {
        warnings.push(`page "${page.id}": repeat.over "${page.repeat.over}" is not an array`);
        continue;
      }
      items.forEach((item, i) => {
        sections.push(
          renderPage(page, component, { ...base, [page.repeat!.as]: item, '@index': i }),
        );
      });
    } else {
      sections.push(renderPage(page, component, base));
    }
  }

  const html = [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<title>${escapeHtml(doc.name)}</title>`,
    `<style>${doc.css}</style>`,
    '</head><body>',
    sections.join('\n'),
    '</body></html>',
  ].join('\n');

  return { html, pageCount: sections.length, warnings };
}
